import { useState } from 'react';
import { wineTastingFaq } from '@wineryxplorer/shared';
import type { FaqBlock } from '@wineryxplorer/shared';
import { PageContainer } from '../components/PageContainer';

const renderBlock = (block: FaqBlock, index: number) => {
  if (typeof block === 'string') {
    return (
      <p key={index} className="text-[15px] font-sans leading-relaxed text-gray-700">
        {block}
      </p>
    );
  }

  const listClasses = `${
    block.ordered ? 'list-decimal' : 'list-disc'
  } pl-5 space-y-1 text-[15px] font-sans leading-relaxed text-gray-700`;

  return block.ordered ? (
    <ol key={index} className={listClasses}>
      {block.items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ol>
  ) : (
    <ul key={index} className={listClasses}>
      {block.items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
};

export const WineTastingFAQ = () => {
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set());

  const toggle = (index: number) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer>
        <div className="max-w-3xl mx-auto py-6">
          <h1 className="font-serif text-3xl text-gray-900 mb-2">
            Frequently Asked Questions About Wine Tasting
          </h1>
          <p className="font-sans text-gray-600 mb-6">
            New to wine tasting? These answers cover what to expect, what to wear, and how to make
            the most of your visit.
          </p>

          <div className="border-t border-gray-200">
            {wineTastingFaq.map((item, index) => {
              const isOpen = openIndices.has(index);
              const panelId = `faq-panel-${index}`;
              const buttonId = `faq-button-${index}`;

              return (
                <div key={index} className="border-b border-gray-200">
                  <button
                    id={buttonId}
                    type="button"
                    onClick={() => toggle(index)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="flex w-full items-center justify-between gap-4 py-4 text-left bg-transparent cursor-pointer"
                  >
                    <span className="font-sans font-semibold text-gray-900">{item.question}</span>
                    <svg
                      className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isOpen && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className="pb-4 space-y-3"
                    >
                      {item.answer.map((block, i) => renderBlock(block, i))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </PageContainer>
    </div>
  );
};
